import { lazy, Suspense, useState } from 'react';
import { Button } from '@ai-tutor/ui';
import { FileText, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import type { Source } from '@/data/fixtures';
import { useSourceFileUrl } from '@/hooks/lesson/useSources';
import {
  ViewerLoading,
  ViewerError,
} from '@/components/features/learning/sources/viewers/shell/ViewerStates';
import { ViewerShell } from '@/components/features/learning/sources/viewers/shell/ViewerShell';

// Heavy react-pdf/pdfjs bundle is code-split - only fetched when a PDF opens.
const PdfDocument = lazy(() => import('./PdfDocument'));

interface PDFViewerProps {
  source: Source;
  // Legacy dispatch-shape callbacks - Download/Open now live in the shell.
  onDownload?: (source: Source) => void;
  onOpenExternal?: (url: string) => void;
}

const MIN_ZOOM = 50;
const MAX_ZOOM = 300;
const STEP = 25;

// `tap-target`: 32px drawn, 44px tapped on a phone; the toolbar widens its
// gaps there so neighbouring areas do not overlap (`ViewerShell`).
const zoomBtn =
  'tap-target h-8 w-8 text-muted-foreground hover:text-foreground';

/** Zoom controls + page indicator, rendered into ViewerShell's toolbarExtra. */
function PdfToolbar({
  zoom,
  setZoom,
  page,
  numPages,
}: {
  zoom: number;
  setZoom: (fn: (z: number) => number) => void;
  page: number;
  numPages: number;
}) {
  return (
    <>
      <Button
        size='icon'
        variant='ghost'
        className={zoomBtn}
        onClick={() => setZoom(z => Math.max(MIN_ZOOM, z - STEP))}
        disabled={zoom <= MIN_ZOOM}
        title='Zoom out'
        aria-label='Zoom out'
      >
        <ZoomOut className='h-4 w-4' />
      </Button>
      <span className='min-w-12 text-center text-xs tabular-nums text-muted-foreground'>
        {zoom}%
      </span>
      <Button
        size='icon'
        variant='ghost'
        className={zoomBtn}
        onClick={() => setZoom(z => Math.min(MAX_ZOOM, z + STEP))}
        disabled={zoom >= MAX_ZOOM}
        title='Zoom in'
        aria-label='Zoom in'
      >
        <ZoomIn className='h-4 w-4' />
      </Button>
      <Button
        size='icon'
        variant='ghost'
        className={zoomBtn}
        onClick={() => setZoom(() => 100)}
        disabled={zoom === 100}
        title='Reset zoom'
        aria-label='Reset zoom'
      >
        <RotateCcw className='h-4 w-4' />
      </Button>
      {numPages > 0 && (
        <span className='ml-1 min-w-16 text-center text-xs tabular-nums text-muted-foreground'>
          {page} / {numPages}
        </span>
      )}
    </>
  );
}

export function PDFViewer({ source }: PDFViewerProps) {
  const { data, isLoading, error } = useSourceFileUrl(source.id);
  const [zoom, setZoom] = useState(100);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [renderFailed, setRenderFailed] = useState(false);

  if (isLoading) return <ViewerLoading />;
  if (error || !data?.url)
    return (
      <ViewerError
        message="Couldn't load PDF"
        detail={(error as Error | undefined)?.message}
      />
    );

  return (
    <ViewerShell
      sourceId={source.id}
      fullscreen
      toolbarExtra={
        <PdfToolbar
          zoom={zoom}
          setZoom={setZoom}
          page={page}
          numPages={numPages}
        />
      }
      icon={FileText}
      label='PDF'
      tone='reference'
    >
      <div className='h-full w-full'>
        {renderFailed ? (
          <ViewerError
            message="Couldn't display this PDF"
            detail='Use Download or Open in new tab (top right) to view it instead.'
          />
        ) : (
          <Suspense fallback={<ViewerLoading />}>
            <PdfDocument
              url={data.url}
              zoom={zoom}
              onLoadSuccess={setNumPages}
              onVisiblePageChange={setPage}
              onFatalError={() => setRenderFailed(true)}
            />
          </Suspense>
        )}
      </div>
    </ViewerShell>
  );
}
